import type {
	RingCentralAuthUrlDto,
	RingCentralDialOptionsModel,
	RingCentralDialResponseDto,
	RingCentralExtensionDto,
	RingCentralUserAuthStatusDto,
	RingCentralUserDeviceDto,
	RingCentralUserDevicesResponseDto,
	RingCentralUserDialStatusDto,
} from '@supply-mover-2-0/shared';
import { ApiService } from './ApiService';

// Type aliases for cleaner exports
export type RingCentralUserDialStatus = RingCentralUserDialStatusDto;
export type RingCentralDialResult = RingCentralDialResponseDto;
export type RingCentralUserDevice = RingCentralUserDeviceDto;
export type RingCentralUserDevicesResult = RingCentralUserDevicesResponseDto;
export type RingCentralDialOptions = RingCentralDialOptionsModel;
export type RingCentralExtension = RingCentralExtensionDto;

/**
 * Service for RingCentral click-to-dial functionality
 */
class RingCentralQueryServiceClass {
	private _cachedStatus: RingCentralUserDialStatusDto | null = null;
	private _cacheTimestamp: number = 0;
	private readonly _cacheDuration = 60000; // 1 minute cache

	/**
	 * Get user dial status - whether the current user can use click-to-dial
	 * Results are cached for 1 minute to avoid excessive API calls
	 */
	public async getUserDialStatus(
		forceRefresh = false,
	): Promise<RingCentralUserDialStatusDto> {
		const now = Date.now();

		if (
			!forceRefresh &&
			this._cachedStatus != null &&
			now - this._cacheTimestamp < this._cacheDuration
		) {
			return this._cachedStatus;
		}

		try {
			const response = await ApiService.apiGet<RingCentralUserDialStatusDto>(
				'/integrations/ringcentral/user-dial-status',
			);

			this._cachedStatus = response;
			this._cacheTimestamp = now;

			return response;
		} catch (error) {
			console.error('Failed to get RingCentral user dial status:', error);

			// Return a safe default on error
			return {
				connected: false,
				canDial: false,
				needsUserAuth: false,
				userAuthenticated: false,
				phoneExtensionId: null,
				phoneExtensionNumber: null,
				callMode: 'ringout',
				userPhoneNumber: null,
			};
		}
	}

	/**
	 * Initiate a dial via RingCentral
	 */
	public async dial(
		phoneNumber: string,
		options?: RingCentralDialOptionsModel,
	): Promise<RingCentralDialResponseDto> {
		try {
			return await ApiService.apiPost<RingCentralDialResponseDto>(
				'/integrations/ringcentral/dial',
				{
					phoneNumber,
					callMethod: options?.callMethod,
					deviceId: options?.deviceId,
					forwardingNumberId: options?.forwardingNumberId,
					ringOutPhoneNumber: options?.ringOutPhoneNumber,
				},
			);
		} catch (error) {
			console.error('Failed to initiate dial:', error);

			return {
				success: false,
				error: 'Failed to initiate call. Please try again.',
				errorCode: 'UNAVAILABLE',
			};
		}
	}

	/**
	 * Clear the cached status
	 */
	public clearCache(): void {
		this._cachedStatus = null;
		this._cacheTimestamp = 0;
	}

	/**
	 * Get user's personal RingCentral authentication status
	 */
	public async getUserAuthStatus(): Promise<RingCentralUserAuthStatusDto> {
		try {
			return await ApiService.apiGet<RingCentralUserAuthStatusDto>(
				'/integrations/ringcentral/user/auth/status',
			);
		} catch (error) {
			console.error('Failed to get RingCentral user auth status:', error);

			return { authenticated: false };
		}
	}

	/**
	 * Get per-user RingCentral authorization URL
	 */
	public async getUserAuthUrl(): Promise<RingCentralAuthUrlDto | null> {
		try {
			return await ApiService.apiGet<RingCentralAuthUrlDto>(
				'/integrations/ringcentral/user/auth/url',
			);
		} catch (error) {
			console.error('Failed to get RingCentral user auth URL:', error);

			return null;
		}
	}

	/**
	 * Handle per-user RingCentral OAuth callback
	 */
	public async handleUserCallback(
		code: string,
		state: string,
	): Promise<boolean> {
		try {
			await ApiService.apiPost<{ success: boolean }>(
				'/integrations/ringcentral/user/auth/callback',
				{ code, state },
			);

			// Clear cache to force refresh of dial status
			this.clearCache();

			return true;
		} catch (error) {
			console.error('Failed to handle RingCentral user callback:', error);

			return false;
		}
	}

	/**
	 * Disconnect user's personal RingCentral connection
	 */
	public async disconnectUser(): Promise<boolean> {
		try {
			await ApiService.apiPost<{ success: boolean }>(
				'/integrations/ringcentral/user/disconnect',
				{},
			);

			// Clear cache to force refresh of dial status
			this.clearCache();

			return true;
		} catch (error) {
			console.error('Failed to disconnect RingCentral user:', error);

			return false;
		}
	}

	/**
	 * Get user's available devices for click-to-dial.
	 *
	 * A RingCentral problem (expired sign-in, missing permission, outage) comes
	 * back in the result's `error`. A failed request is NOT turned into an empty
	 * list here: that is how an expired sign-in used to read as "No devices
	 * found" (PD-16786). It rejects, and the dialog shows it.
	 */
	public async getUserDevices(): Promise<RingCentralUserDevicesResponseDto> {
		return ApiService.apiGet<RingCentralUserDevicesResponseDto>(
			'/integrations/ringcentral/user/devices',
		);
	}

	/**
	 * Get available RingCentral extensions for mapping
	 */
	public async getExtensions(): Promise<Array<RingCentralExtensionDto>> {
		try {
			return await ApiService.apiGet<Array<RingCentralExtensionDto>>(
				'/integrations/ringcentral/extensions',
			);
		} catch (error) {
			console.error('Failed to get RingCentral extensions:', error);

			return [];
		}
	}
}

export const RingCentralQueryService = new RingCentralQueryServiceClass();
